import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  filterLines,
  getMobileDiscipline,
  mobileAppBase,
  neighborsInList,
} from './progressCatalog';
import {
  markStayOnJobs,
  pathAfterDisciplineContinue,
  pathAfterDisciplineOpen,
  pathAfterJobContinue,
  pathAfterJobOpen,
  readLastDiscipline,
  readLastRow,
  rememberLastDiscipline,
  rememberLastRow,
  shouldAutoOpenOnlyJob,
  shouldStayOnJobs,
} from './saveQueue';
import { useProgressSession } from './useProgressSession';
import { useModuleSession } from './useModuleSession';
import { rememberFieldRoute } from './fieldRouteMemory';
import { fieldAdminPath, openInsideNativeApp } from './nativeShell';
import { MOBILE_APP_NAME } from './appBranding';
import { SettingsScreen } from './SettingsScreen';
import { usePhoneTheme } from './theme';
import { usePermissions } from '../../hooks/usePermissions';
import { getJobPhoneModule, isJobPhoneModule } from './moduleCatalog';
import {
  DisciplineListScreen,
  HomeMenuScreen,
  LineDetailScreen,
  LineListScreen,
  ProjectListScreen,
  ScopeLandingScreen,
} from './screens';
import {
  getEstimateMobileMenu,
  getHomeDepartments,
} from './mobileMenuAvailability';
import { ModuleDetailScreen, ModuleListScreen } from './ModuleScreens';
import { TimeTrackerScreen } from './TimeTrackerScreen';
import { CodingStructureScreen } from './CodingStructureScreen';
import ScanCapture from './ScanCapture';
import './mobile.css';

export const MobileProgressApp = ({ demo = false, user = null }) => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, disciplineId, rowId, deptId, estimateId } = useParams();
  const base = mobileAppBase(location.pathname);
  const isDemo = demo || base === '/m/preview';
  const permissions = usePermissions(user);
  // Preview also exposes Admin so we can demo the shell without Azure.
  // Live /m still gates on canAccessAdminPanel (superadmin / System).
  const showAdmin = isDemo || permissions.canAccessAdminPanel;
  const adminPath = isDemo ? `${base}/admin` : fieldAdminPath();

  const session = useProgressSession({ instance, accounts, demo: isDemo });
  const moduleSession = useModuleSession({ instance, accounts, demo: isDemo });
  const {
    loadRows: loadModuleRows,
    saveField: saveModuleField,
  } = moduleSession;
  const {
    loadProjects,
    loadEstimates,
    loadRows,
    loadPhotos,
    uploadPhoto,
    updateStep,
    updateComment,
    updateHold,
    undoLast,
    pinJob,
    flushQueue,
  } = session;
  const [query, setQuery] = useState('');
  const [lineQuery, setLineQuery] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [workFilter, setWorkFilter] = useState('needs');
  const [scanOpen, setScanOpen] = useState(false);
  const [lastDiscipline, setLastDiscipline] = useState(() => readLastDiscipline(projectId));
  const [lastRowId, setLastRowId] = useState(() => readLastRow(projectId, disciplineId));
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const { theme, typeSize, font } = usePhoneTheme();

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prevHref = link?.getAttribute('href');
    const prevTitle = document.title;
    const apple = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevApple = apple?.getAttribute('content');
    if (link) link.setAttribute('href', `${process.env.PUBLIC_URL || ''}/manifest-field.json`);
    document.title = MOBILE_APP_NAME;
    if (apple) apple.setAttribute('content', MOBILE_APP_NAME);
    return () => {
      if (link && prevHref) link.setAttribute('href', prevHref);
      document.title = prevTitle;
      if (apple && prevApple) apple.setAttribute('content', prevApple);
    };
  }, []);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    rememberFieldRoute(location.pathname, location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    loadProjects();
    loadEstimates();
  }, [loadEstimates, loadProjects]);

  useEffect(() => {
    if (projectId && disciplineId && !isJobPhoneModule(disciplineId)) {
      loadRows(projectId, disciplineId);
    }
  }, [disciplineId, loadRows, projectId]);

  const jobModule = getJobPhoneModule(disciplineId);
  const homeModule = location.pathname.endsWith('/settings') || location.pathname.endsWith('/preferences')
    ? 'settings'
    : location.pathname.endsWith('/time')
      ? 'time'
      : location.pathname.endsWith('/coding')
        ? 'coding'
        : /\/jobs\/?$/.test(location.pathname)
          ? 'jobs'
          : deptId
            ? 'dept'
            : estimateId
              ? 'estimate'
              : null;

  useEffect(() => {
    if (projectId && jobModule) {
      loadModuleRows(projectId, jobModule.id);
    }
  }, [jobModule, loadModuleRows, projectId]);

  useEffect(() => {
    setLineQuery('');
    setFilterValue('');
    setWorkFilter('needs');
  }, [disciplineId, projectId]);

  useEffect(() => {
    setLastDiscipline(readLastDiscipline(projectId));
    setLastRowId(readLastRow(projectId, disciplineId));
  }, [disciplineId, projectId]);

  useEffect(() => {
    if (projectId && disciplineId && rowId && !jobModule) {
      rememberLastRow(projectId, disciplineId, rowId);
      setLastRowId(String(rowId));
      const current = session.rows.find((item) => String(item.id) === String(rowId));
      if (current) loadPhotos(projectId, disciplineId, current);
    }
  }, [disciplineId, jobModule, loadPhotos, projectId, rowId, session.rows]);

  const go = (path) => navigate(`${base}${path}`);

  // Job → trackers → ISOs → detail. Never skip levels on a normal tap.
  // Continue cards deep-link to the last walked place.
  const openJob = (item) => {
    pinJob(item);
    go(pathAfterJobOpen(item.id));
  };

  const continueJob = (item) => {
    pinJob(item);
    const remembered = readLastDiscipline(item.id);
    const lastIso = remembered ? readLastRow(item.id, remembered) : null;
    go(pathAfterJobContinue(item.id, { lastDiscipline: remembered, lastRow: lastIso }));
  };

  const openDiscipline = (id) => {
    rememberLastDiscipline(projectId, id);
    setLastDiscipline(id);
    go(pathAfterDisciplineOpen(projectId, id));
  };

  const openModule = (id) => {
    go(`/job/${projectId}/${id}`);
  };

  const openHomeModule = (route) => {
    go(`/${route}`);
  };

  const continueDiscipline = (id) => {
    rememberLastDiscipline(projectId, id);
    setLastDiscipline(id);
    go(pathAfterDisciplineContinue(projectId, id, readLastRow(projectId, id)));
  };

  useEffect(() => {
    if (homeModule !== 'jobs') return;
    const only = shouldAutoOpenOnlyJob({
      projectId,
      loading: session.loadingProjects,
      query,
      stayOnJobs: shouldStayOnJobs(),
      projects: session.projects,
    });
    if (only) openJob(only);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeModule, projectId, query, session.loadingProjects, session.projects]);

  const project = useMemo(
    () => session.projects.find((item) => String(item.id) === String(projectId)) || (
      projectId ? { id: projectId, job_number: '', job_name: '' } : null
    ),
    [projectId, session.projects]
  );

  const visibleProjects = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return session.projects;
    return session.projects.filter((item) => (
      String(item.job_number || '').toLowerCase().includes(needle)
      || String(item.job_name || '').toLowerCase().includes(needle)
    ));
  }, [query, session.projects]);

  let discipline = null;
  try {
    discipline = disciplineId && !jobModule ? getMobileDiscipline(disciplineId) : null;
  } catch {
    discipline = null;
  }

  const walkRows = useMemo(() => {
    if (!discipline) return [];
    return filterLines(session.rows, discipline, {
      query: lineQuery,
      filterValue,
      needsWork: workFilter !== 'all' && workFilter !== 'hold',
      scope: workFilter === 'shop' || workFilter === 'field' ? workFilter : 'all',
      onHoldOnly: workFilter === 'hold',
    });
  }, [discipline, filterValue, lineQuery, session.rows, workFilter]);

  const row = session.rows.find((item) => String(item.id) === String(rowId));
  const neighbors = neighborsInList(walkRows, session.rows, rowId);
  const moduleRow = moduleSession.rows.find((item) => String(item.id) === String(rowId));
  const moduleNeighbors = neighborsInList(moduleSession.rows, moduleSession.rows, rowId);
  const switchJob = () => {
    markStayOnJobs();
    go('/jobs');
  };

  return (
    <div
      className="lgl-m-app"
      data-theme={theme}
      data-type-size={typeSize}
      data-font={font}
      data-demo={isDemo ? 'true' : 'false'}
    >
      {isDemo ? (
        <div className="lgl-m-banner">
          Preview on an S22 Ultra-sized screen. Nothing here writes to a real job.
        </div>
      ) : null}
      {online === false ? (
        <div className="lgl-m-banner">No signal. Percents and photos stay on this phone until it comes back.</div>
      ) : null}

      {homeModule === 'settings' ? <SettingsScreen onBack={() => go('')} /> : null}

      {homeModule === 'time' ? (
        <TimeTrackerScreen
          accounts={accounts}
          demo={isDemo}
          instance={instance}
          projects={session.projects}
          onBack={() => go('/dept/accounting')}
        />
      ) : null}

      {homeModule === 'coding' ? <CodingStructureScreen onBack={() => go('/dept/accounting')} /> : null}

      {!homeModule && !projectId && !deptId && !estimateId ? (
        <ScopeLandingScreen
          projects={session.projects}
          estimates={session.estimates}
          departments={getHomeDepartments({ includeAdmin: showAdmin })}
          loadingProjects={session.loadingProjects}
          loadingEstimates={session.loadingEstimates}
          queuedCount={session.queuedCount}
          queuedPhotoCount={session.queuedPhotoCount}
          online={online}
          onSelectProject={(id) => {
            const item = session.projects.find((project) => String(project.id) === String(id));
            if (item) {
              pinJob(item);
              navigate(`${base}/job/${item.id}`, { state: { from: 'landing' } });
            }
          }}
          onSelectEstimate={(id) => go(`/estimate/${id}`)}
          onSelectDepartment={(id) => go(`/dept/${id}`)}
          onRetryQueue={flushQueue}
          onOpenSettings={() => go('/preferences')}
          onOpenWebsite={() => { openInsideNativeApp('/'); }}
        />
      ) : null}

      {homeModule === 'dept' ? (
        <HomeMenuScreen
          includeAdmin={showAdmin}
          departmentId={deptId}
          title={getHomeDepartments({ includeAdmin: true }).find((item) => item.id === deptId)?.label || 'Department'}
          subtitle="Items in this department. Phone-ready ones open here; the rest stay on the desktop."
          queuedCount={session.queuedCount}
          queuedPhotoCount={session.queuedPhotoCount}
          online={online}
          onBack={() => go('')}
          onOpenHomeRoute={(route) => {
            if (route === 'admin') {
              navigate(adminPath);
              return;
            }
            openHomeModule(route);
          }}
          onRetryQueue={flushQueue}
          onOpenWebsite={() => { openInsideNativeApp('/'); }}
        />
      ) : null}

      {homeModule === 'estimate' ? (
        <HomeMenuScreen
          menu={getEstimateMobileMenu()}
          title={
            session.estimates.find((item) => String(item.id) === String(estimateId))?.estimate_number
            || 'Estimate'
          }
          subtitle="Estimate menus for this bid. Phone-ready items open here; the rest stay on the desktop."
          queuedCount={session.queuedCount}
          queuedPhotoCount={session.queuedPhotoCount}
          online={online}
          onBack={() => go('')}
          onOpenHomeRoute={(route) => {
            if (route === 'admin') {
              navigate(adminPath);
              return;
            }
            openHomeModule(route);
          }}
          onRetryQueue={flushQueue}
          onOpenWebsite={() => { openInsideNativeApp('/'); }}
        />
      ) : null}

      {homeModule === 'jobs' ? (
        <ProjectListScreen
          projects={visibleProjects}
          lastJob={session.lastJob}
          queuedCount={session.queuedCount}
          queuedPhotoCount={session.queuedPhotoCount}
          loading={session.loadingProjects}
          error={session.error}
          query={query}
          online={online}
          onQuery={setQuery}
          onOpen={openJob}
          onContinue={continueJob}
          onRetryQueue={flushQueue}
          onRefresh={loadProjects}
          onBack={() => go('/dept/projects')}
          onOpenWebsite={() => { openInsideNativeApp('/'); }}
        />
      ) : null}

      {!homeModule && projectId && !disciplineId ? (
        <DisciplineListScreen
          project={project}
          lastDiscipline={lastDiscipline}
          onBack={() => {
            if (location.state?.from === 'landing') {
              go('');
              return;
            }
            markStayOnJobs();
            go('/jobs');
          }}
          onOpen={openDiscipline}
          onContinue={continueDiscipline}
          onOpenModule={openModule}
          onOpenWebsiteTracker={(url) => { openInsideNativeApp(url); }}
        />
      ) : null}

      {!homeModule && projectId && jobModule && !rowId ? (
        <ModuleListScreen
          project={project}
          module={jobModule}
          rows={moduleSession.rows}
          loading={moduleSession.loadingRows}
          error={moduleSession.error}
          onBack={() => go(`/job/${projectId}`)}
          onOpen={(id) => go(`/job/${projectId}/${disciplineId}/${id}`)}
          onRefresh={() => loadModuleRows(projectId, jobModule.id)}
        />
      ) : null}

      {!homeModule && projectId && jobModule && rowId ? (
        <ModuleDetailScreen
          project={project}
          module={jobModule}
          row={moduleRow}
          neighbors={moduleNeighbors}
          saveState={moduleSession.saveState}
          onBack={() => go(`/job/${projectId}/${disciplineId}`)}
          onNeighbor={(id) => go(`/job/${projectId}/${disciplineId}/${id}`)}
          onSaveField={(currentRow, fieldKey, value) => (
            saveModuleField(projectId, jobModule.id, currentRow, fieldKey, value)
          )}
        />
      ) : null}

      {!homeModule && projectId && discipline && !rowId ? (
        <LineListScreen
          project={project}
          discipline={discipline}
          rows={session.rows}
          loading={session.loadingRows}
          error={session.error}
          query={lineQuery}
          filterValue={filterValue}
          workFilter={workFilter}
          lastRowId={lastRowId}
          onQuery={setLineQuery}
          onFilter={setFilterValue}
          onWorkFilter={setWorkFilter}
          onBack={() => go(`/job/${projectId}`)}
          onOpen={(id) => go(`/job/${projectId}/${disciplineId}/${id}`)}
          onSwitchJob={switchJob}
          onRefresh={() => loadRows(projectId, disciplineId)}
          onScan={() => setScanOpen(true)}
        />
      ) : null}

      {scanOpen && !homeModule && projectId && discipline && !rowId ? (
        <ScanCapture
          open={scanOpen}
          discipline={discipline}
          rows={session.rows}
          onClose={() => setScanOpen(false)}
          onMatch={(matched) => {
            setScanOpen(false);
            if (matched?.id != null) {
              go(`/job/${projectId}/${disciplineId}/${matched.id}`);
            }
          }}
        />
      ) : null}

      {!homeModule && projectId && discipline && rowId ? (
        <LineDetailScreen
          project={project}
          discipline={discipline}
          row={row}
          neighbors={neighbors}
          saveState={session.saveState}
          photos={session.rowPhotos}
          photoStatus={session.photoStatus}
          queuedCount={session.queuedCount + session.queuedPhotoCount}
          canUndo={session.canUndo}
          onBack={() => go(`/job/${projectId}/${disciplineId}`)}
          onChangeStep={(id, columnId, value) => updateStep(projectId, disciplineId, id, columnId, value)}
          onChangeComment={(id, comments) => updateComment(projectId, disciplineId, id, comments)}
          onToggleHold={(id, onHold) => updateHold(projectId, disciplineId, id, onHold)}
          onNeighbor={(id) => go(`/job/${projectId}/${disciplineId}/${id}`)}
          onPhoto={(file) => uploadPhoto(projectId, disciplineId, row, file)}
          onRetryQueue={flushQueue}
          onUndo={undoLast}
          onSwitchJob={switchJob}
        />
      ) : null}

      {user?.display_name && !isDemo ? (
        <p className="lgl-m-empty">{user.display_name}</p>
      ) : null}
    </div>
  );
};

export default MobileProgressApp;
